import React, { useCallback, useEffect, useState } from 'react'
import type { SeatData, SeatDisplayStatus, VenueSectionData, SelectedSeat } from './SeatMap.types'
import { resolveColiseoZone, type ColiseoArea } from './coliseoZones'

const SEAT_SIZE = 18
const SEAT_GAP = 4
const STEP = SEAT_SIZE + SEAT_GAP // 22 — butaca legible; 48 de CANCHA Norte ≈ 1080px, entra en el modal
const LABEL = 16
const AISLE = 12

const COLORS: Record<SeatDisplayStatus, string> = {
  AVAILABLE: '#22c55e', SOLD: '#ef4444', RESERVED_TEMP: '#f59e0b',
  SELECTED: '#eab308', BLOCKED_ADMIN: '#374151', OFFLINE: '#4b5563',
}
const SECTION_COLORS: Record<string, string> = {
  green: '#22c55e', blue: '#3b82f6', purple: '#a855f7', orange: '#f97316', pink: '#ec4899',
}
const CURSORS: Record<SeatDisplayStatus, string> = {
  AVAILABLE: 'pointer', SOLD: 'not-allowed', RESERVED_TEMP: 'not-allowed',
  SELECTED: 'pointer', BLOCKED_ADMIN: 'not-allowed', OFFLINE: 'default',
}

function seatFill(ds: SeatDisplayStatus, sectionColor?: string): string {
  if (ds === 'AVAILABLE' && sectionColor && SECTION_COLORS[sectionColor]) return SECTION_COLORS[sectionColor]
  return COLORS[ds]
}

const SeatNode = React.memo(function SeatNode({
  seat, x, y, ds, sectionColor, onClick,
}: {
  seat: SeatData; x: number; y: number; ds: SeatDisplayStatus; sectionColor?: string; onClick: () => void
}) {
  const interactive = ds === 'AVAILABLE' || ds === 'SELECTED'
  return (
    <g
      role="button"
      aria-label={`Asiento ${seat.label} — ${ds}`}
      aria-pressed={ds === 'SELECTED'}
      tabIndex={interactive ? 0 : -1}
      opacity={ds === 'OFFLINE' ? 0.35 : 1}
      style={{ outline: 'none' }}
      onClick={interactive ? onClick : undefined}
      onKeyDown={(e) => { if (interactive && (e.key === 'Enter' || e.key === ' ')) onClick() }}
    >
      <rect x={x} y={y} width={SEAT_SIZE} height={SEAT_SIZE} rx={4}
        fill={seatFill(ds, sectionColor)} style={{ cursor: CURSORS[ds] }} />
      <text x={x + SEAT_SIZE / 2} y={y + SEAT_SIZE / 2 + 3} textAnchor="middle"
        fontSize={7} fontWeight="600" fill={ds === 'AVAILABLE' ? '#14532d' : 'white'}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {seat.seatNumber}
      </text>
    </g>
  )
})

interface SectionBlockProps {
  section: VenueSectionData
  orientation: 'horizontal' | 'vertical'
  reverseRows: boolean
  /** Invierte el orden vertical de los asientos (CANCHA Oeste: 5 abajo → 28 arriba). */
  reverseVertical?: boolean
  /** Etiqueta de fila a mostrar en vez de row.rowLabel (ej. CANCHA usa N/O/E por zona). */
  rowLabelOverride?: string
  selectedIds: Set<number>
  conflictIds: Set<number>
  onSeatClick: (seat: SeatData) => void
}

function SectionBlock({ section, orientation, reverseRows, reverseVertical, rowLabelOverride, selectedIds, conflictIds, onSeatClick }: SectionBlockProps) {
  const rows = [...section.rows].sort((a, b) => a.rowLabel.localeCompare(b.rowLabel))
  if (reverseRows) rows.reverse()

  const maxSeats = Math.max(...rows.map((r) => r.seats.length), 1)
  const statusOf = (seat: SeatData): SeatDisplayStatus =>
    conflictIds.has(seat.id) ? 'SOLD' : selectedIds.has(seat.id) ? 'SELECTED' : seat.status

  const width = orientation === 'horizontal' ? LABEL + maxSeats * STEP + AISLE : rows.length * STEP + 6
  const height = orientation === 'horizontal' ? rows.length * STEP + 6 : LABEL + maxSeats * STEP + AISLE

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline gap-2">
        <span className="text-sm font-semibold text-secondary-120">{section.localityName ?? section.name}</span>
        {!section.localityName && section.subSection && <span className="text-xs text-white/60">{section.subSection}</span>}
      </div>
      <svg width={width} height={height} role="group"
        aria-label={`Sección ${section.name}${section.subSection ? ' ' + section.subSection : ''}`}
        style={{ display: 'block', overflow: 'visible' }}>
        {rows.map((row, ri) => {
          const aisleIdx = Math.floor(row.seats.length / 2)
          // CANCHA Oeste: invertir para que el nº menor quede abajo (continuidad del aro 5→100).
          const orderedSeats = orientation === 'vertical' && reverseVertical ? [...row.seats].reverse() : row.seats
          return (
            <g key={row.rowLabel}>
              {orientation === 'horizontal' ? (
                <text x={LABEL - 4} y={ri * STEP + SEAT_SIZE / 2 + 4} textAnchor="end" fontSize={11} fill="white">{rowLabelOverride ?? row.rowLabel}</text>
              ) : (
                <text x={ri * STEP + SEAT_SIZE / 2} y={LABEL - 4} textAnchor="middle" fontSize={11} fill="white">{rowLabelOverride ?? row.rowLabel}</text>
              )}
              {orderedSeats.map((seat, i) => {
                const shift = i >= aisleIdx ? AISLE : 0
                const x = orientation === 'horizontal' ? LABEL + i * STEP + shift : ri * STEP
                const y = orientation === 'horizontal' ? ri * STEP : LABEL + i * STEP + shift
                return (
                  <SeatNode key={seat.id} seat={seat} x={x} y={y} ds={statusOf(seat)}
                    sectionColor={row.color ?? section.color} onClick={() => onSeatClick(seat)} />
                )
              })}
            </g>
          )
        })}
      </svg>
    </div>
  )
}

export interface ColiseoLayoutProps {
  sections: VenueSectionData[]
  selectedSeats: SelectedSeat[]
  onSeatClick: (seat: SeatData) => void
  onConflictRef?: (flash: (seatId: number) => void) => void
}

export function ColiseoLayout({ sections, selectedSeats, onSeatClick, onConflictRef }: ColiseoLayoutProps) {
  const [conflictIds, setConflictIds] = useState<Set<number>>(new Set())

  const flashConflict = useCallback((seatId: number) => {
    setConflictIds(new Set([seatId]))
    setTimeout(() => setConflictIds(new Set()), 2000)
  }, [])
  useEffect(() => { onConflictRef?.(flashConflict) }, [onConflictRef, flashConflict])

  const handleSeatClick = useCallback((seat: SeatData) => {
    setConflictIds(new Set()); onSeatClick(seat)
  }, [onSeatClick])

  const selectedIds = new Set(selectedSeats.map((s) => s.seatId))

  const byArea = new Map<ColiseoArea, VenueSectionData[]>()
  const unpositioned: VenueSectionData[] = []
  for (const s of sections) {
    const zone = resolveColiseoZone(s)
    if (zone) {
      if (!byArea.has(zone.area)) byArea.set(zone.area, [])
      byArea.get(zone.area)!.push(s)
    } else {
      unpositioned.push(s)
    }
  }

  const renderArea = (area: ColiseoArea) => {
    const secs = byArea.get(area)
    if (!secs) return null
    const vertical = area === 'cancha-o' || area === 'cancha-e'
    const reverseRows = area === 'pref-norte' // F al fondo (arriba), D pegada a la cancha (abajo)
    // CANCHA usa la zona como etiqueta de fila (N/O/E) para no repetir "A" en las 3 secciones.
    const rowLabelOverride =
      area === 'cancha-n' ? 'N' : area === 'cancha-o' ? 'O' : area === 'cancha-e' ? 'E' : undefined
    return (
      <div style={{ gridArea: area }} className={vertical ? 'flex flex-row gap-3 items-center justify-center' : 'flex flex-col gap-3 items-center'}>
        {secs.map((sec) => (
          <SectionBlock key={sec.id} section={sec} orientation={vertical ? 'vertical' : 'horizontal'}
            reverseRows={reverseRows} reverseVertical={area === 'cancha-o'} rowLabelOverride={rowLabelOverride} selectedIds={selectedIds} conflictIds={conflictIds} onSeatClick={handleSeatClick} />
        ))}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4" data-testid="coliseo-layout">
      <div style={{ width: 'fit-content', margin: '0 auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateAreas: `". pref-norte ." ". cancha-n ." "cancha-o court cancha-e" ". pref-sur ."`,
          gridTemplateColumns: 'auto auto auto',
          gap: '10px 14px',
          alignItems: 'center',
          justifyItems: 'center',
        }}>
          {renderArea('pref-norte')}
          {renderArea('cancha-n')}
          {renderArea('cancha-o')}
          <div style={{ gridArea: 'court', width: '100%', alignSelf: 'stretch' }} data-testid="coliseo-court">
            <img
              src="/images/cancha-coliseo.png"
              alt="Cancha"
              className="block h-full w-full rounded-lg"
              style={{ objectFit: 'fill', minHeight: 170 }}
            />
          </div>
          {renderArea('cancha-e')}
          {renderArea('pref-sur')}
        </div>
      </div>

      {unpositioned.length > 0 && (
        <div className="flex flex-wrap justify-center gap-6">
          {unpositioned.map((sec) => (
            <SectionBlock key={sec.id} section={sec} orientation="horizontal" reverseRows={false}
              selectedIds={selectedIds} conflictIds={conflictIds} onSeatClick={handleSeatClick} />
          ))}
        </div>
      )}
    </div>
  )
}

export default ColiseoLayout
